import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { X, Download } from "lucide-react";

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

const DISMISSED_KEY = "pk-install-dismissed-at";
const COOLDOWN_DAYS = 14;

export const InstallPrompt = () => {
  const [deferred, setDeferred] = useState<BeforeInstallPromptEvent | null>(null);
  const [show, setShow] = useState(false);

  useEffect(() => {
    // Already installed (running standalone)?
    const standalone =
      window.matchMedia("(display-mode: standalone)").matches ||
      (window.navigator as any).standalone === true;
    if (standalone) return;

    // Cooldown check
    const dismissed = Number(localStorage.getItem(DISMISSED_KEY) || 0);
    const cooldownMs = COOLDOWN_DAYS * 24 * 60 * 60 * 1000;
    if (dismissed && Date.now() - dismissed < cooldownMs) return;

    const handler = (e: Event) => {
      e.preventDefault();
      setDeferred(e as BeforeInstallPromptEvent);
      // Delay so it doesn't pop immediately on landing
      setTimeout(() => setShow(true), 8000);
    };

    window.addEventListener("beforeinstallprompt", handler as EventListener);
    return () => window.removeEventListener("beforeinstallprompt", handler as EventListener);
  }, []);

  const dismiss = () => {
    localStorage.setItem(DISMISSED_KEY, String(Date.now()));
    setShow(false);
  };

  const install = async () => {
    if (!deferred) return;
    await deferred.prompt();
    await deferred.userChoice;
    setDeferred(null);
    setShow(false);
  };

  if (!show || !deferred) return null;

  return (
    <div
      role="dialog"
      aria-label="Install Param Kesar app"
      className="fixed bottom-4 left-4 right-4 md:left-auto md:right-6 md:bottom-6 md:max-w-sm z-50 bg-card border border-border rounded-lg shadow-lg p-4 animate-fade-in"
    >
      <div className="flex items-start gap-3">
        <img src="/icon-192.png" alt="Param Kesar®" className="h-12 w-12 rounded-md flex-shrink-0" width={48} height={48} loading="lazy" decoding="async" />
        <div className="flex-1 min-w-0">
          <p className="font-medium text-sm">Install Param Kesar®</p>
          <p className="text-xs text-muted-foreground mt-0.5">
            Add to your home screen for one-tap reorders.
          </p>
          <div className="flex gap-2 mt-3">
            <Button size="sm" onClick={install} className="h-8">
              <Download className="h-3.5 w-3.5 mr-1.5" />
              Install
            </Button>
            <Button size="sm" variant="ghost" onClick={dismiss} className="h-8">
              Not now
            </Button>
          </div>
        </div>
        <button
          aria-label="Dismiss"
          onClick={dismiss}
          className="text-muted-foreground hover:text-foreground -mt-1 -mr-1 p-1"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
};
